import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, CircularProgress, Grid, Stack,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faMobileScreenButton, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Cookies from 'js-cookie';
import BlockTitle from '../BlockTitle/BlockTitle';
import BlockImage from '../BlockImage/BlockImage';
import BlockSlot, { AddPosition, BlockSlotPanelEventType } from './BlockSlot';
import BlockTextWithBackground from '../BlockTextWithBackground/BlockTextWithBackground';
import BlockImageAndText from '../BlockImageAndText/BlockImageAndText';
import BlockMenuList3 from '../BlockMenuList/BlockMenuList';
import BlockMap from '../BlockMap/BlockMap';
import BlockFooter from '../BlockFooter/BlockFooter';
import { BlockDataPerPage, PagesContext, PageSetting } from '../../../contexts/PagesContext';
import DataManager from '../../../managers/DataManager';
import { ImagesContext } from '../../../contexts/ImagesContext';
import BlockHeader from '../Headers/BlockHeader/BlockHeader';
import BlockData from '../../../models/BlockData';
import BlockType from '../../../models/BlockType';
import BlockText from '../BlockText/BlockText';
import BlockTextList from '../BlockTextList/BlockTextList';
import BlockButton from '../BlockButton/BlockButton';
import { PreviewModeType } from '../../../models/BlockProps';
import BlockHtml from '../BlockHtml/BlockHtml';
import BlockCard from '../BlockCard/BlockCard';
import BlockCardCentered from '../BlockCardCentered/BlockCardCentered';
import BlockHeaderWithLogo from '../Headers/BlockHeaderWithLogo/BlockHeaderWithLogo';
import styles from './BlocksContainer.module.css';
import BlockTitleTextButton from '../BlockTitleTextButton/BlockTitleTextButton';
import BlockHeaderLogoMenu from '../Headers/BlockHeaderLogoMenu/BlockHeaderLogoMenu';
import BlockButtonList from '../BlockButtonList/BlockButtonList';
import { BasicMenuItemData } from '../../Menu';
import BlockCarouselWithPureJs from '../BlockCarousel/BlockCarouselWithPureJs';
import BlockImageList from '../BlockImageList/BlockImageList';
import { HTMLExportContext } from '../../../contexts/HtmlExportContext';
import BlockTable from '../BlockTable/BlockTable';
import BlockCenteredImage from '../BlockCenteredImage/BlockCenteredImage';
import BlockFullBleedImage from '../BlockFullBleedImage/BlockFullBleedImage';
import BlockLinedTitle from '../BlockLinedTitle/BlockLinedTitle';
import { LayoutContext } from '../../../contexts/LayoutContext';
import MainPageLayout from '../../../layout/MainPageLayout';
import BlockiFrame from '../BlockiFrame/BlockiFrame';
import HtmlExportButton from '../../Buttons/HtmlExportButton';
import BlockSelectionModal from '../../../pages/Master/BlocksManagement/BlocksSelectionModal';
import { ImageTypeValue } from '../../../utils/image';
import ImageSelectionModal from '../../Modals/ImageModal/ImageSelectionModal';
import BlockSNSPreview from '../BlockSNS/BlockSNSPreview';
import BlockSNSEdit from '../BlockSNS/BlockSNSEdit';
import { getTemplate } from '../../../services/template';
import BlockSNSOldPreview from '../BlockSNSOld/BlockSNSOldPreview';
import BlockSNSOldEdit from '../BlockSNSOld/BlockSNSOldEdit';
import { useSelectedElement } from '../../../hooks/UseSelectedElement';
import BlockAnimationBlock from '../BlockAnimationBlocks/BlockAnimationBlock';
import config from '../../../config';
import { decrypt } from '../../../utils/encryption';
import AdContainer from '../../Ads/AdContainer';
import BlockContact from '../BlockContact/BlockContact';
import BlockAnimationWithDescriptionBlock from '../BlockAnimationWithImageDescription/BlockAnimationWithImageDescription';
import { BlockAlign, BlockJustify, BlockSeparator } from '../../../models/BlockSettings';
import BlockVideo from '../BlockVideo/BlockVideo';
import { VideoContext } from '../../../contexts/VideoContext';
import BlockMenuListWithColumns from '../BlockMenuListWithColumns/BlockMenuListWithColumns';
import BlockMenuListWithEqualSpace from '../BlockMenuListWithEqualSpace/BlockMenuListWithEqualSpace';
import BlockHtmlSafe from '../BlockHtmlSafe/BlockHtmlSafe';
import BlockiFrameSafe from '../BlockiFrameSafe/BlockiFrameSafe';
import BlockiFrameServices from '../BlockiFrameServices/BlockiFrameServices';
import BlockMenuListFixedMax2 from '../BlockMenuListFixedMax2/BlockMenuListFixedMax2';
import BlockMenuListFixedMax3 from '../BlockMenuListFixedMax3/BlockMenuListFixedMax3';
import BlockMenuListFixedMobile2 from '../BlockMenuListFixedMobile2/BlockMenuListFixedMobile2';
import BlockMenuListEqualMobile2 from '../BlockMenuListEqualMobile2/BlockMenuListEqualMobile2';
import BlockMenuListEqualMax2 from '../BlockMenuListEqualMax2/BlockMenuListEqualMax2';
import BlockMenuListEqualMax3 from '../BlockMenuListEqualMax3/BlockMenuListEqualMax3';

interface BlocksContainerProps {
  page?: BlockDataPerPage,
  blocksData: BlockData[]
}

// dataManagerはuseRefで管理されてあるため、dataManagerへの操作はschedulerをトリガーして、画面をレンダリングさせません、そのため、強制アップデートするためのhookを作ります。
// react18はbatched updateがあるため、useStateで取り扱うデータはtrue->falseにしたら、batch updateで結局更新されないため、累積の数値にします。そうすると、batched updatedにしても、必ず、アップデートがありと検知され、再レンダリングさせます。
function useForceUpdate() {
  const [, setState] = useState(0);
  return useCallback(() => setState((v) => v + 1), []);
}

const blockTypeRange: Array<'system-block' | 'my-block'> = ['system-block', 'my-block'];

function BlocksContainer(props: BlocksContainerProps) {
  const { page, blocksData } = props;
  const { page_setting = {} } = page;
  const { animation } = page_setting;
  const { background_color } = page_setting;
  const { main_color } = page_setting;
  const { text_color } = page_setting;
  const { text_font } = page_setting;
  const pageId = page?.id;
  const containerTopRef = useRef<HTMLDivElement>(null);
  const containerBottomRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewModeType>('pc');
  const [imageModalShow, setImageModalShow] = useState(false);
  const [marginModalTrigger, setMarginModalTrigger] = useState(0);
  const [animationModalTrigger, setAnimationModalTrigger] = useState(0);
  const [seoModalTrigger, setSEOModalTrigger] = useState(0);
  const [currentAddPosition, setCurrentAddPosition] = useState(AddPosition.TOP); // 現在「ブロック追加をクリックしたブロックの上なのか、下なのか」
  const [currentAddIndex, setCurrentAddIndex] = useState(0); // 現在「ブロック追加をクリックしたブロックのindex」
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showLinesOptionsModal, setShowLinesOptionsModal] = useState<string | null>(null);
  const [showAlignmentOptionsModal, setShowAlignmentOptionsModal] = useState(false);
  const navigate = useNavigate();
  const [lineOptionSelectedIndex, setLineOptionSelectedIndex] = useState<number | null>(null);
  const [displaySettingsSelectedIndex, setDisplaySettingsSelectedIndex] = useState<number | null>(null);
  const pagesContext = useContext(PagesContext);
  const { addLayout, removeLayout, changeShowSidebar } = useContext(LayoutContext);
  const dataManager = useRef<DataManager>(new DataManager());
  const blockItems = dataManager.current.getCurrent();
  const { GetImages } = useContext(ImagesContext);
  const { fetchVideos } = useContext(VideoContext);
  const forceUpdate = useForceUpdate();
  const {
    UpdateSetting, Get, Create, Update, updatePageLoading,
  } = pagesContext;
  const htmlExportContext = useContext(HTMLExportContext);

  const [initialBlockItems, setInitialBlockItems] = useState<any[]>(blocksData.length === undefined ? blockItems : blocksData);

  const [isSettingToEdit, setIsSettingToEdit] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setIsSettingToEdit(false);
    }
  }, [isEditing]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const { cookieName } = config.auth;
      const cookiesList = Cookies.get();
      const cookieValue = cookiesList[cookieName];
      const decrypted = cookieValue ? JSON.parse(decrypt(cookieValue)) : null;

      if ((JSON.stringify(blockItems) !== JSON.stringify(initialBlockItems)) && decrypted && blockItems.length > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [blockItems, initialBlockItems]);

  const [blockSelectionOpen, setBlockSelectionOpen] = useState(false);

  useEffect(() => {
    dataManager.current.loadBlocks(blocksData);
    forceUpdate();
  }, [blocksData, dataManager, forceUpdate]);

  // fetch user's images
  useEffect(() => {
    GetImages(ImageTypeValue.MY_IMAGE);
  }, [GetImages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      containerBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
  };

  const scrollToTop = () => {
    setTimeout(() => {
      containerTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
  };

  const canUndo = useCallback((): boolean => dataManager.current?.canUndo(), [dataManager]);

  const panelEventHandler = useCallback((id: string, event: BlockSlotPanelEventType) => {
    switch (event) {
      case BlockSlotPanelEventType.UP:
        dataManager.current?.moveUp(id);
        forceUpdate();
        break;
      case BlockSlotPanelEventType.DOWN:
        dataManager.current?.moveDown(id);
        forceUpdate();
        break;
      case BlockSlotPanelEventType.TOP:
        dataManager.current?.moveTop(id);
        forceUpdate();
        break;
      case BlockSlotPanelEventType.BOTTOM:
        dataManager.current?.moveBottom(id);
        forceUpdate();
        break;
      case BlockSlotPanelEventType.DUPLICATE:
        dataManager.current?.duplicate(id);
        forceUpdate();
        break;
      case BlockSlotPanelEventType.DELETE:
        dataManager.current?.delete(id);
        forceUpdate();
        break;
      default:
        break;
    }
  }, [dataManager, forceUpdate]);

  const updateData = useCallback((id: string, data: any) => {
    dataManager.current.updateData(id, data);
    forceUpdate();
  }, [dataManager, forceUpdate]);

  const undo = useCallback(() => {
    dataManager.current?.undo();
    forceUpdate();
    forceUpdate();
  }, [dataManager, forceUpdate]);

  const handleAddBlock = (block: BlockData, position?: AddPosition, index?: number, fromMenu?: boolean): void => {
    switch (block.type) {
      case BlockType.HEADER_WITH_LOGO:
      case BlockType.HEADER_WITH_LOGO_FREE:
      case BlockType.HEADER_LOGO_MENU:
      case BlockType.HEADER_LOGO_MENU_FREE:
      case BlockType.HEADER:
      case BlockType.HEADER_FREE:
      case BlockType.FOOTER:
      case BlockType.FOOTER_FREE:
        // check if there is default settings for the menu
        if (!block.data.settings) {
          block.data.settings = {
            align: BlockAlign.CENTER,
            justify: BlockJustify.CENTER,
            separator: BlockSeparator.NONE,
          };
        }
        break;
      default:
        // Add your code here for the default case
        break;
    }
    if (position === AddPosition.TOP) {
      dataManager.current?.addBlock({ ...block, id: uuid() }, index);
    } else if (position === AddPosition.BOTTOM) {
      dataManager.current?.addBlock({ ...block, id: uuid() }, index + 1);
    } else {
      dataManager.current?.addBlock({ ...block, id: uuid() });
    }
    forceUpdate();
    if (!fromMenu) return null;
    if (block.type === BlockType.HEADER) return scrollToTop();
    if (block.type === BlockType.HEADER_WITH_LOGO) return scrollToTop();
    if (block.type === BlockType.HEADER_LOGO_MENU) return scrollToTop();
    return scrollToBottom();
  };

  const editHandler = (id: string, data: any) => {
    updateData(id, data);
  };

  const onMenuItemClick = (block: BlockData) => {
    handleAddBlock(block, currentAddPosition, currentAddIndex);
  };

  const boxWidth = useMemo(() => {
    if (previewMode === 'pc') {
      return '100%';
    }

    return 375;
  }, [previewMode]);

  const SettingMenuItems: BasicMenuItemData[] = useMemo(() => [
    {
      id: '1',
      onClick: () => {
        if (!isSaving) {
          setMarginModalTrigger((t) => t + 1);
        }
      },
      text: 'コンテンツサイズ設定',
    },
    {
      id: '2',
      onClick: () => {
        if (!isSaving) {
          setSEOModalTrigger((t) => t + 1);
        }
      },
      text: 'head＆body設定',
    },
    {
      id: '3',
      onClick: () => setAnimationModalTrigger((t) => t + 1),
      text: 'アニメーション設定',
    },
  ], [isSaving]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [previewMode]);

  useEffect(() => {
    addLayout(
      <MainPageLayout
        pageId={pageId}
        blockItems={blockItems}
        initialBlockItems={initialBlockItems}
        settings={page?.page_setting}
        handleSettingSave={(settings: PageSetting, cb, ccb) => {
          UpdateSetting(pageId, {
            title: settings.title,
            description: settings.description,
            keywords: settings.keywords,
            favicon_file: settings.favicon_file,
            header_script: settings.header_script,
            body_script: settings.body_script,
            smart_phone_width: settings.smart_phone_width,
            desk_top_width: settings.desk_top_width,
            google_analytics_script: settings.google_analytics_script,
            is_robots_noindex: settings.is_robots_noindex,
            url: settings.url,
            custom_url: settings.custom_url,
            sub_pages: settings.sub_pages,
            animation: settings.animation,
            google_search_console_url: settings.google_search_console_url,
            background_color: settings.background_color,
            main_color: settings.main_color,
            text_color: settings.text_color,
            text_font: settings.text_font,
            password: settings.password,
            auth_page_description: settings.auth_page_description,
            should_deactivate_password_protection: settings.should_deactivate_password_protection,
          }, cb, ccb);
        }}
        handleUndo={undo}
        handleEdit={(e) => {
          if (!isEditing) {
            setIsSettingToEdit(true);
          }
          setTimeout(() => {
            setIsEditing(e);
          }, 0);
        }}
        handleSave={(e) => {
          setInitialBlockItems(blockItems);
          setIsSaving(e);
          dataManager.current.resetUndo();
        }}
        isEditing={isEditing}
        isSaving={isSaving}
        canUndo={(canUndo() || false)}
        renderHtmlExportButton={() => (
          <HtmlExportButton
            loading={updatePageLoading || htmlExportContext.loading}
            handleOnExport={(html: Blob) => {
              // またページ自体作られてない状態で、exportした場合
              if (pageId == null) {
                Create(JSON.stringify(blockItems), (id: string) => {
                  htmlExportContext.postExportHTML(html, id);
                  navigate(`/pages/${id}`);
                });
                return;
              }
              // ページも作られた状態で、exportした場合
              Update(pageId, JSON.stringify(blockItems), () => {
                htmlExportContext.postExportHTML(html, pageId);
              });
            }}
          />
        )}
        renderSmartPhoneButton={() => (
          <Box sx={{ marginLeft: 2 }}>
            モード
            <Button
              sx={{ marginX: 0.5 }}
              startIcon={<FontAwesomeIcon style={{ fontSize: '14px' }} icon={faDesktop} />}
              className={`btn btn-outline-secondary ${previewMode === 'pc' ? 'active' : ''}`}
              onClick={() => setPreviewMode('pc')}
            >
              PC
            </Button>
            <Button
              startIcon={<FontAwesomeIcon style={{ fontSize: '14px' }} icon={faMobileScreenButton} />}
              className={`btn btn-outline-secondary ${previewMode === 'smartphone' ? 'active' : ''}`}
              onClick={() => setPreviewMode('smartphone')}
            >
              スマホ
            </Button>
          </Box>
        )}
      />,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, dataManager.current.timeline.length, addLayout, pageId, SettingMenuItems, undo, canUndo, page, seoModalTrigger, UpdateSetting, Get, marginModalTrigger, previewMode, updatePageLoading, htmlExportContext.loading]);

  useEffect(() => {
    changeShowSidebar(false);
    return () => {
      removeLayout();
    };
  }, [changeShowSidebar, removeLayout]);

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleBlockAddClicked = useCallback((position: AddPosition, index: number) => {
    setBlockSelectionOpen(true);
    setCurrentAddPosition(position);
    setCurrentAddIndex(index);
  }, []);

  const { selectedElementId, setSelectedElementId } = useSelectedElement();

  function waitForElementAndScrollToIt(id: string | null) {
    if (!id) {
      // console.error('No selectedElementId available.');
      return;
    }

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            observer.disconnect();
            setSelectedElementId(null);
          }
        }
      });
    });

    // Start observing changes to the DOM
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  waitForElementAndScrollToIt(selectedElementId);

  return (
    <>
      <ImageSelectionModal
        open={imageModalShow}
        handleClose={() => {
          setImageModalShow(false);
        }}
        handleSave={() => {
          // TODO: figure out usecase;
          setImageModalShow(false);
        }}
      />
      {/* Put BlockSelectionModal here */}
      <BlockSelectionModal
        title="ブロック追加"
        open={blockSelectionOpen}
        range={blockTypeRange}
        handleClose={() => setBlockSelectionOpen(false)}
        handleTemplateSelected={(t) => {
          getTemplate(t.id).then((response) => {
            onMenuItemClick(response.data.data.template);
            setBlockSelectionOpen(false);
          });
        }}
      />
      <Box
        width="100%"
        height="100vh"
        display="flex"
        flexDirection="column"
        m="auto"
        overflow="auto"
        className={styles.container}
        sx={{
          backgroundColor: isSaving ? 'lightGray' : '#f5f7fb',
        }}
      >
        {isSaving ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '60px' }} />
          </Box>
        ) : (
          <Grid container id="blocksContainer">
            <Grid style={{ height: '100%', width: '100%' }} sx={{ display: 'flex', justifyContent: 'center', padding: isEditing ? 2 : '' }}>
              <Box width={boxWidth}>
                <Grid container id="blocksContainer" style={{ height: '100%', width: '100%' }} justifyContent="center">
                  <Box
                    sx={{
                      width: '100%',
                      p: isEditing ? 1 : null,
                      border: isEditing ? '1px solid lightGray' : null,
                      backgroundColor: '#fff',
                      boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {(blockItems.length === 0 && isEditing) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          height: '80vh',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {/* 画面上に何もない時のブロック追加ボタン */}
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ width: 200 }}
                          onClick={() => {
                            setBlockSelectionOpen(true);
                          }}
                          startIcon={<AddIcon />}
                        >
                          ブロック追加
                        </Button>
                      </Box>
                    ) : (
                      <Stack
                        style={{ backgroundColor: background_color, color: text_color, fontFamily: text_font }}
                      >
                        {isSettingToEdit && (
                          <div style={{ padding: '10px', display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={40} />
                          </div>
                        )}
                        <div ref={containerTopRef} key="list-top" />
                        {blockItems.map((item, index) => {
                          item.data.has_custom_url = !!page_setting?.custom_url;

                          switch (item.type) {
                            case BlockType.HEADER:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                >
                                  <BlockHeader
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    backgroundColor={background_color ?? '#ffff'}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.HEADER_FREE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockHeader
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    backgroundColor={background_color ?? '#ffff'}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.HEADER_WITH_LOGO:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                >
                                  <BlockHeaderWithLogo
                                    mainColor={main_color}
                                    backgroundColor={background_color ?? '#ffff'}
                                    fontFamily={text_font}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.HEADER_WITH_LOGO_FREE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockHeaderWithLogo
                                    mainColor={main_color}
                                    backgroundColor={background_color ?? '#ffff'}
                                    fontFamily={text_font}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.HEADER_LOGO_MENU:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                >
                                  <BlockHeaderLogoMenu
                                    backgroundColor={background_color ?? '#ffff'}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.HEADER_LOGO_MENU_FREE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isHeader={!item.data?.isFree}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockHeaderLogoMenu
                                    backgroundColor={background_color ?? '#ffff'}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.TITLE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => setSelectedIndex(index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTitle
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.IMAGE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => setSelectedIndex(index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockImage
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    blockData={blockItems}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.TXT_BACKGROUND:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTextWithBackground
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.TXT_BACKGROUND_TALL:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTextWithBackground
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.IMAGE_TEXT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockImageAndText
                                    blockData={blockItems}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.MENU_LIST_3:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockMenuList3
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.MAP:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockMap data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.I_FRAME:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockiFrame data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.I_FRAME_SAFE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockiFrameSafe data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.I_FRAME_SERVICES:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockiFrameServices data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.TEXT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockText onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.TEXT_LIST:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTextList onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.BUTTON:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockButton mainColor={main_color} onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} blockData={blockItems} />
                                </BlockSlot>
                              );
                            case BlockType.HTML:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockHtml data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.HTML_SAFE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockHtmlSafe data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} />
                                </BlockSlot>
                              );
                            case BlockType.CARD_LEFT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCard
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CARD_RIGHT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCard
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CARD_LEFT_CENTERED:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCardCentered
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CARD_RIGHT_CENTERED:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCardCentered
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.TITLE_TXT_BUTTON:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTitleTextButton mainColor={main_color} onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} blockData={blockItems} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.TITLE_TXT_BUTTON_TALL:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTitleTextButton onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} blockData={blockItems} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.BUTTON_LIST:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockButtonList mainColor={main_color} onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} blockData={blockItems} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.CAROUSEL:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCarouselWithPureJs
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CAROUSEL_TALL:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCarouselWithPureJs
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CAROUSEL_FIXED:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCarouselWithPureJs
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CAROUSEL_FIXED_TALL:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCarouselWithPureJs
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.IMAGE_LIST:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockImageList onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} blockData={blockItems} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.TABLE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTable onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.CENTERED_IMAGE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCenteredImage
                                    showImageModal={index === selectedIndex}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.TABLE_BOTTOM_BORDER:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTable onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.TABLE_NO_BORDER:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockTable onCloseImageModal={() => setSelectedIndex(null)} showImageModal={index === selectedIndex} data={item} isEditing={isEditing} isTemplate={false} editHandler={editHandler} previewMode={previewMode} />
                                </BlockSlot>
                              );
                            case BlockType.FULL_BLEED_IMAGE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockFullBleedImage
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    blockData={blockItems}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.FULL_BLEED_CARD_LEFT_CENTERED:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCardCentered
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    isFullBleed
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.FULL_BLEED_CARD_RIGHT_CENTERED:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isFullBleed
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockCardCentered
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    isFullBleed
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.LINED_TITLE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  showOptionsForLines
                                  handleLinesOptionsClicked={() => { setLineOptionSelectedIndex(index); }}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockLinedTitle
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showLineOptionsModal={index === lineOptionSelectedIndex}
                                    onCloseLineOptionsModal={() => setLineOptionSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.SNS_OLD:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  showOptionsForAlignment
                                  handleAlignmentOptionsClicked={() => { setShowAlignmentOptionsModal(true); }}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                >
                                  {!isEditing && <BlockSNSOldPreview data={item} isEditing={false} isTemplate={false} editHandler={editHandler} />}
                                  {isEditing
                                    && (
                                      <BlockSNSOldEdit
                                        onCloseImageModal={() => setSelectedIndex(null)}
                                        showImageModal={index === selectedIndex}
                                        data={item}
                                        isEditing
                                        isTemplate={false}
                                        editHandler={editHandler}
                                        blockItems={blockItems}
                                        showAlignmentOptionsModal={showAlignmentOptionsModal}
                                        onCloseAlignmentModal={() => { setShowAlignmentOptionsModal(false); }}
                                      />
                                    )}
                                </BlockSlot>
                              );
                            case BlockType.SNS:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  showOptionsForAlignment
                                  handleAlignmentOptionsClicked={() => { setShowAlignmentOptionsModal(true); }}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  {!isEditing && <BlockSNSPreview data={item} isEditing={false} isTemplate={false} editHandler={editHandler} previewMode={previewMode} />}
                                  {isEditing && (
                                    <BlockSNSEdit
                                      onCloseImageModal={() => setSelectedIndex(null)}
                                      showImageModal={index === selectedIndex}
                                      data={item}
                                      isEditing
                                      isTemplate={false}
                                      editHandler={editHandler}
                                      blockItems={blockItems}
                                      previewMode={previewMode}
                                      showAlignmentOptionsModal={showAlignmentOptionsModal}
                                      onCloseAlignmentModal={() => { setShowAlignmentOptionsModal(false); }}
                                    />
                                  )}
                                </BlockSlot>
                              );
                            case BlockType.ANIMATION_COMPONENT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation=""
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockAnimationBlock
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.ANIMATION_WITH_DESCRIPTION_COMPONENT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation=""
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockAnimationWithDescriptionBlock
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.CONTACT:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation=""
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockContact
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.FOOTER:
                            case BlockType.FOOTER_FREE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isFooter={!item.data?.isFree}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockFooter
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.VIDEO:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  isStaticInBackground={item.data?.video?.settings?.staticBackground}
                                  handlePanelEvent={panelEventHandler}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  isFullBleed
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockVideo
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_WITH_COLUMNS:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockMenuListWithColumns
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_WITH_EQUAL_SPACE:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                >
                                  <BlockMenuListWithEqualSpace
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_FIXED_MAX_2:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListFixedMax2
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_FIXED_MAX_3:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListFixedMax3
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_FIXED_MOBILE_2:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListFixedMobile2
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_EQUAL_MOBILE_2:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListEqualMobile2
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_EQUAL_MAX_2:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListEqualMax2
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            case BlockType.BLOCK_MENU_LIST_EQUAL_MAX_3:
                              return (
                                <BlockSlot
                                  key={item.id}
                                  dataId={item.id}
                                  isEditing={isEditing}
                                  handlePanelEvent={panelEventHandler}
                                  handleImageIconClicked={() => {
                                    setSelectedIndex(index);
                                  }}
                                  handleBlockAddClicked={(position) => handleBlockAddClicked(position, index)}
                                  animation={animation}
                                  defaultBackgroundColor={background_color ?? '#fff'}
                                  showOptionsForDisplaySettings
                                  handleDisplaySettingsClicked={() => { setDisplaySettingsSelectedIndex(index); }}
                                >
                                  <BlockMenuListEqualMax3
                                    mainColor={main_color}
                                    onCloseImageModal={() => setSelectedIndex(null)}
                                    showImageModal={index === selectedIndex}
                                    showOptionsForCols={index === displaySettingsSelectedIndex}
                                    onCloseColsOptionsModal={() => setDisplaySettingsSelectedIndex(null)}
                                    data={item}
                                    isEditing={isEditing}
                                    isTemplate={false}
                                    editHandler={editHandler}
                                    blockData={blockItems}
                                    previewMode={previewMode}
                                  />
                                </BlockSlot>
                              );
                            default:
                              return (
                                <div />
                              );
                          }
                        })}
                        <div ref={containerBottomRef} key="list-bottom" />
                      </Stack>
                    )}
                  </Box>
                </Grid>
              </Box>
            </Grid>
            <AdContainer />
          </Grid>
        )}
      </Box>
    </>
  );
}

export default BlocksContainer;
